/* ==========================================================================
   Self sign-up for new employees (shown inside the Login card).
   The request goes to the company MD / CEO for approval (Employees → Sign-up requests);
   no account exists until it is approved. Backend: routes.js  /auth/signup-options, /auth/signup
   ========================================================================== */
import React, { useEffect, useState } from 'react';
import { User, Mail, Phone, Lock, Building2, Eye, EyeOff, AlertCircle, ArrowLeft, Briefcase } from 'lucide-react';
import { API_BASE } from './config';

const EMPTY = { name: '', email: '', phone: '', company_id: '', designation: '', department: '', joining_date: '', password: '', confirm: '', website: '' };
const digits = (v) => String(v || '').replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '');

function validate(v) {
  const e = {};
  if (v.name.trim().length < 2) e.name = 'Enter your full name';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = 'Enter a valid email, e.g. name@company.com';
  if (!/^[6-9]\d{9}$/.test(digits(v.phone))) e.phone = 'Enter a 10-digit mobile number';
  if (!v.company_id) e.company_id = 'Choose your company';
  if (v.password.length < 8) e.password = 'At least 8 characters';
  else if (!/[A-Za-z]/.test(v.password) || !/\d/.test(v.password)) e.password = 'Use at least one letter and one number';
  else if (v.password.toLowerCase() === 'password123') e.password = 'Choose a less common password';
  if (!e.password && v.confirm !== v.password) e.confirm = 'The passwords don’t match';
  return e;
}

export default function SignUp({ onDone, onBack }) {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [options, setOptions] = useState(null); // { enabled, companies, message }
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/auth/signup-options`)
      .then((r) => r.json().catch(() => ({})).then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (alive) setOptions(ok ? d : { enabled: false, companies: [], message: d.error || 'The sign-up form could not be loaded.' }); })
      .catch(() => { if (alive) setOptions({ enabled: false, companies: [], message: 'Could not reach the server. Check your internet connection.' }); });
    return () => { alive = false; };
  }, []);

  const set = (k) => (e) => {
    const val = e.target.value;
    setValues((v) => ({ ...v, [k]: val }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError('');
    const errs = validate(values);
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) { document.getElementById(`su-${first}`)?.focus(); return; }
    setSaving(true);
    try {
      const body = { ...values, email: values.email.trim().toLowerCase(), phone: digits(values.phone), company_id: Number(values.company_id) };
      delete body.confirm;
      const res = await fetch(`${API_BASE}/api/auth/signup`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setFormError(data.error || `Your registration could not be sent (error ${res.status}).`); return; }
      onDone(data.message || 'Your registration has been sent for approval.', body.email);
    } catch (err) {
      setFormError('Could not reach the server. Check your internet connection and try again.');
    } finally { setSaving(false); }
  };

  const field = (k, label, input, hint) => (
    <div className="hr-field">
      <label className="hr-label" htmlFor={`su-${k}`}>{label}</label>
      {input}
      {errors[k] ? <span className="su-err" id={`su-${k}-err`}>{errors[k]}</span> : hint ? <span className="hr-hint">{hint}</span> : null}
    </div>
  );
  const aria = (k) => ({ id: `su-${k}`, 'aria-invalid': !!errors[k], 'aria-describedby': errors[k] ? `su-${k}-err` : undefined });

  if (options && !options.enabled) {
    return (
      <div>
        <h2>Create your account</h2>
        <div className="hr-alert hr-alert-info" role="status" style={{ marginTop: 20 }}>
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{options.message || 'Self-registration is not available yet. Please ask HR to add you.'}</span>
        </div>
        <button type="button" className="hr-btn hr-btn-secondary hr-btn-block" style={{ marginTop: 20 }} onClick={onBack}><ArrowLeft size={16} /> Back to sign in</button>
      </div>
    );
  }

  return (
    <div>
      <h2>Create your account</h2>
      <p style={{ color: 'var(--muted)', marginTop: 6, marginBottom: 22 }}>
        New to the group? Register here. Your company’s MD or CEO approves it, then you sign in with this email and password.
      </p>

      {formError && (
        <div className="hr-alert" role="alert" style={{ marginBottom: 18 }}>
          <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{formError}</span>
        </div>
      )}

      <form onSubmit={submit} noValidate className="su-form">
        {field('name', 'Full name', (
          <div className="hr-input-icon"><User size={16} />
            <input {...aria('name')} className="hr-input" style={{ height: 44 }} value={values.name} onChange={set('name')} autoComplete="name" placeholder="As on your ID" />
          </div>
        ))}
        {field('email', 'Work email', (
          <div className="hr-input-icon"><Mail size={16} />
            <input {...aria('email')} type="email" className="hr-input" style={{ height: 44 }} value={values.email} onChange={set('email')} autoComplete="email" placeholder="name@company.com" />
          </div>
        ), 'You’ll use this to sign in')}
        {field('phone', 'Mobile number', (
          <div className="hr-input-icon"><Phone size={16} />
            <input {...aria('phone')} type="tel" inputMode="numeric" className="hr-input" style={{ height: 44 }} value={values.phone} onChange={set('phone')} autoComplete="tel" placeholder="98765 43210" />
          </div>
        ))}
        {field('company_id', 'Company', (
          <div className="hr-input-icon"><Building2 size={16} />
            <select {...aria('company_id')} className="hr-select su-select" value={values.company_id} onChange={set('company_id')} disabled={!options}>
              <option value="">{options ? 'Choose your company' : 'Loading…'}</option>
              {(options?.companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        ))}
        <div className="su-row">
          {field('designation', 'Job title (optional)', (
            <div className="hr-input-icon"><Briefcase size={16} />
              <input {...aria('designation')} className="hr-input" style={{ height: 44 }} value={values.designation} onChange={set('designation')} placeholder="e.g. Sales Executive" />
            </div>
          ))}
          {field('joining_date', 'Date of joining (optional)', (
            <input {...aria('joining_date')} type="date" className="hr-input" style={{ height: 44 }} value={values.joining_date} onChange={set('joining_date')} />
          ))}
        </div>
        {field('department', 'Department (optional)', (
          <input {...aria('department')} className="hr-input" style={{ height: 44 }} value={values.department} onChange={set('department')} placeholder="e.g. Sales" />
        ))}
        {field('password', 'Password', (
          <div className="hr-input-icon su-pw"><Lock size={16} />
            <input {...aria('password')} type={showPw ? 'text' : 'password'} className="hr-input" style={{ height: 44 }} value={values.password} onChange={set('password')} autoComplete="new-password" placeholder="At least 8 characters" />
            <button type="button" className="su-eye" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}>{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
          </div>
        ), 'Use letters and numbers')}
        {field('confirm', 'Confirm password', (
          <div className="hr-input-icon"><Lock size={16} />
            <input {...aria('confirm')} type={showPw ? 'text' : 'password'} className="hr-input" style={{ height: 44 }} value={values.confirm} onChange={set('confirm')} autoComplete="new-password" placeholder="Type it again" />
          </div>
        ))}
        {/* Bot trap: hidden from people and screen readers */}
        <div className="su-trap" aria-hidden="true">
          <label htmlFor="su-website">Website</label>
          <input id="su-website" tabIndex={-1} autoComplete="off" value={values.website} onChange={set('website')} />
        </div>

        <button type="submit" disabled={saving || !options} className="hr-btn hr-btn-primary hr-btn-lg hr-btn-block" style={{ marginTop: 4 }}>
          {saving ? 'Sending…' : 'Send for approval'}
        </button>
      </form>

      <p className="hr-hint" style={{ marginTop: 20, textAlign: 'center' }}>
        Already have an account? <button type="button" className="su-link" onClick={onBack}>Sign in</button>
      </p>
    </div>
  );
}
